import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { Card, Button, Badge, Spinner } from 'react-bootstrap';
import axios from 'axios';
import './SubscriptionWidget.css';

const SubscriptionWidget = () => {
  // Auth Context
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Subscription Data State
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Load initial subscription data
  useEffect(() => {
    if (auth && auth.vendorId) {
      fetchSubscriptionStatus();
    }
  }, [auth]);

  // API Calls
  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/subscriptions/${auth.vendorId}`);
      setSubscription(response.data.subscription);
      setLoading(false);
    } catch (err) {
      console.error("Subscription error:", err);
      setError('Failed to load subscription status');
      setLoading(false);
    }
  };

  // Event Handlers
  const handleNavigateToSubscriptionPage = () => {
    navigate('/vendor/subscription');
  };

  // Helper Functions
  // Get display status - handle canceled subscriptions with pending cancel dates
  const getDisplayStatus = () => {
    if (!subscription) return null;
    
    if (subscription.canceled_date || subscription.status === 'CANCELED') {
      return 'CANCELED';
    }
    return subscription.status;
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PENDING': return 'warning';
      case 'CANCELED': return 'danger';
      case 'PAUSED': return 'info';
      default: return 'secondary';
    }
  };

  // Main widget render
  if (loading || !auth) {
    return (
      <Card className="mb-4">
        <Card.Body className="text-center">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading subscription...
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Subscription</Card.Title>
          <div className="text-danger mb-2">{error}</div>
          <Button 
            variant="link" 
            onClick={fetchSubscriptionStatus} 
            className="p-0"
          >
            Retry
          </Button>
        </Card.Body>
      </Card>
    );
  }

  if (!subscription) {
    return (
      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Subscription Required</Card.Title>
          <div className="alert alert-warning">
            Your account requires an active subscription to continue using all Weedhurry features.
          </div>
          <Button onClick={handleNavigateToSubscriptionPage} variant="success" className="d-block w-100">
            Subscribe Now
          </Button>
        </Card.Body>
      </Card>
    );
  }

  // If subscription exists, show status
  const displayStatus = getDisplayStatus();
  
  return (
    <Card className="mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Card.Title className="mb-0">Subscription</Card.Title>
          <Badge bg={getStatusBadgeVariant(displayStatus)}>
            {displayStatus}
          </Badge>
        </div>
        
        <hr className="my-3" />
        
        <div className="mb-3">
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">Plan:</span>
            <span className="fw-medium">Weedhurry Vendor</span>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">Price:</span>
            <span className="fw-medium">$79/month</span>
          </div>
          {subscription.start_date && (
            <div className="d-flex justify-content-between">
              <span className="text-muted">Started:</span>
              <span>{new Date(subscription.start_date).toLocaleDateString()}</span>
            </div>
          )}
          {subscription.canceled_date && (
            <div className="d-flex justify-content-between text-danger">
              <span className="text-muted">Ends On:</span>
              <span>{new Date(subscription.canceled_date).toLocaleDateString()}</span>
            </div>
          )}
        </div>
        
        <Button onClick={handleNavigateToSubscriptionPage} variant="primary" className="d-block w-100">
          Manage Subscription
        </Button>
      </Card.Body>
    </Card>
  );
};

export default SubscriptionWidget;